import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { reputations } from '@/mocks/comics';

function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website_alt: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotValue = new FormData(form).get('website_alt') as string;
    if (honeypotValue && honeypotValue.trim() !== '') {
      setFormStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '', website_alt: '' });
      setTimeout(() => setFormStatus('idle'), 5000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const submitAddr = 'https://readdy.ai/api/form/d9pf1k1n2trhb35gve8g';
      const payload = new FormData(form);
      payload.delete('website_alt');

      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '', website_alt: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else if (serverMsg && (String(serverMsg).includes('spam') || String(serverMsg).includes('form data is spam'))) {
        setFormStatus('error');
        setFormError('提交失败，请稍后重试。');
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试。');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="bg-background-50 rounded-lg p-6 md:p-8" id="contact-form">
      <h3 className="text-lg font-semibold text-foreground-900 mb-6">发送消息</h3>

      {formStatus === 'success' && (
        <div className="mb-6 p-4 bg-accent-100 text-accent-700 rounded-lg text-sm">
          消息已发送成功！我们会尽快与您联系，感谢您的关注。
        </div>
      )}

      {formStatus === 'error' && formError && (
        <div className="mb-6 p-4 bg-primary-100 text-primary-700 rounded-lg text-sm">
          {formError}
        </div>
      )}

      <form onSubmit={handleSubmit} data-readdy-form="">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
              您的姓名
            </label>
            <input
              id="contact-name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
              placeholder="请输入您的姓名"
            />
          </div>
          <div>
            <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
              电子邮箱
            </label>
            <input
              id="contact-email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
              placeholder="请输入您的邮箱"
            />
          </div>
        </div>

        <div className="mb-4">
          <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
            主题
          </label>
          <input
            id="contact-subject"
            type="text"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            required
            className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
            placeholder="请输入消息主题"
          />
        </div>

        <div className="mb-4">
          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
            留言内容
          </label>
          <textarea
            id="contact-message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={5}
            maxLength={500}
            className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200 resize-none"
            placeholder="请输入您的留言内容（最多500字）"
          />
          <p className="mt-1 text-xs text-foreground-400 text-right">{formData.message.length}/500</p>
        </div>

        {/* Honeypot */}
        <input
          type="text"
          name="website_alt"
          value={formData.website_alt}
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="website-alt-field"
        />

        <button
          type="submit"
          disabled={formStatus === 'submitting'}
          className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 whitespace-nowrap cursor-pointer"
        >
          {formStatus === 'submitting' ? '发送中...' : '发送消息'}
        </button>
      </form>
    </div>
  );
}

export default function About() {
  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 韩漫画网',
    description: '韩漫画网致力于为广大漫画爱好者提供优质的韩国漫画在线阅读服务。了解我们的品牌故事、用户口碑和联系方式。',
    url: 'https://hanmanhua.com/about',
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫画网',
      url: 'https://hanmanhua.com',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <Navbar />

      <main className="pt-20">
        <div className="py-6 md:py-8 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="mt-6">
              <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900">
                关于我们
              </h1>
              <p className="mt-2 text-sm md:text-base text-foreground-500">
                了解韩漫画网的品牌故事与发展历程
              </p>
            </div>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-12 md:py-16 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-5">
                  品牌故事
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    韩漫画网成立于2022年，由一群热爱韩国漫画的资深漫迷共同创立。我们的初心很简单：为中文读者打造一个高质量的韩国漫画在线阅读平台。
                  </p>
                  <p>
                    经过多年的积累与发展，韩漫画网现已汇聚了海量优质韩漫资源，涵盖了恋爱、奇幻、动作、悬疑、喜剧、剧情、恐怖等丰富多样的类型。无论是热门的连载作品，还是经典的高分佳作，您都可以在这里找到。
                  </p>
                  <p>
                    我们始终坚持"免费、优质、便捷"的服务理念，所有漫画资源均为免费在线观看，无需注册即可畅享阅读。同时，我们的编辑团队每日保持同步更新，确保您能够第一时间追更最新的漫画章节。
                  </p>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Korean%20webtoon%20digital%20art%20studio%20workspace%2C%20warm%20cozy%20creative%20environment%20with%20drawing%20tablet%20and%20sketches%2C%20soft%20natural%20lighting%2C%20modern%20minimal%20interior%2C%20clean%20artistic%20atmosphere%2C%20no%20people&width=800&height=600&seq=hanman-about-brand&orientation=landscape"
                  alt="韩漫画网品牌理念"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center mb-10">
              为什么选择韩漫画网
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-money-cny-circle-line text-2xl"></i>
                </div>
                <h3 className="mt-4 text-sm font-semibold text-foreground-900">完全免费</h3>
                <p className="mt-2 text-xs text-foreground-500 leading-relaxed">
                  所有漫画资源免费观看，无需付费
                </p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className="ri-speed-up-line text-2xl"></i>
                </div>
                <h3 className="mt-4 text-sm font-semibold text-foreground-900">极速更新</h3>
                <p className="mt-2 text-xs text-foreground-500 leading-relaxed">
                  每日同步更新，第一时间获取最新内容
                </p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                  <i className="ri-smartphone-line text-2xl"></i>
                </div>
                <h3 className="mt-4 text-sm font-semibold text-foreground-900">移动适配</h3>
                <p className="mt-2 text-xs text-foreground-500 leading-relaxed">
                  手机平板完美适配，随时随地阅读
                </p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-shield-check-line text-2xl"></i>
                </div>
                <h3 className="mt-4 text-sm font-semibold text-foreground-900">高清画质</h3>
                <p className="mt-2 text-xs text-foreground-500 leading-relaxed">
                  所有漫画均采用高清画质，阅读体验出色
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="py-12 md:py-16 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center mb-10">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {reputations.map((rep, index) => (
                <div key={index} className="bg-background-50 rounded-lg p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: rep.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{rep.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={rep.avatar}
                        alt={rep.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{rep.name}</p>
                      <p className="text-xs text-foreground-400">{rep.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center mb-10">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900">电子邮箱</h3>
                      <p className="text-sm text-foreground-500 mt-0.5">contact@hanmanhua.com</p>
                      <p className="text-xs text-foreground-400 mt-1">商务合作、版权问题请通过邮件联系</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900">服务时间</h3>
                      <p className="text-sm text-foreground-500 mt-0.5">每日更新 全年无休</p>
                      <p className="text-xs text-foreground-400 mt-1">工作日客服在线时间：9:00 - 18:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                      <i className="ri-question-answer-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900">常见问题</h3>
                      <p className="text-sm text-foreground-500 mt-0.5">
                        建议先查看<a href="/#faq" className="text-primary-500 hover:text-primary-600 underline">常见问题</a>页面
                      </p>
                      <p className="text-xs text-foreground-400 mt-1">您的问题可能已经有答案了</p>
                    </div>
                  </div>
                </div>
              </div>
              <ContactForm />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}